//ICS-233
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';


import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from './OperationalPeriodScheduleControl';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import {
  openInstantiatedIncidentTask,
  useInstantiateIncidentTask,
} from 'components/IAP/hooks/incidentActionHooks';
import { useHwcgFlag } from 'utils/featureFlags';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ActionsWorkspace from 'components/IAP/ActionsWorkspace';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { getIncidentCardTitle } from '../USCGPage';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from './icsBoxPrimitives';
import {
  ICS233_BOX_LABELS,
  ICS233_INCIDENT_LOCATION_HINT,
  ICS233_INCIDENT_NAME_HINT,
  ICS233_OPERATIONAL_PERIOD_HINT,
} from './ics233BoxLabels';
import { ICS_233_EXPORT_FORMNAME } from './ics233Export';

/**
 * Open actions tracker, the ICS 233-CG.
 *
 * Boxes 1-3 are the read-only header the printed form repeats; boxes 4-10 are
 * the columns of the action table, which `ActionsWorkspace` owns.
 *
 * The form has no prepared-by box, so it has no signature slot and the banner
 * here offers history alone. Nothing on this card records a version either:
 * the 233 is a projection of the tasks, so the task writers stamp versions
 * server-side whenever an edit changes what the sheet would print.
 */
const OpenActionsTracker: React.FC = () => {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { isFuture } = useICSContext();
  const { isMapVisible } = useUscgMapContext();

  const hwcg = useHwcgFlag();
  const { instantiate, isPending: creatingTask } = useInstantiateIncidentTask();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionEntity | null>(
    null
  );

  const addAction = () => {
    if (!hwcg) {
      setShowAddDrawer(true);
      return;
    }
    if (creatingTask) return;
    void openInstantiatedIncidentTask({
      instantiate,
      status: 'Planned',
      onOpen: setSelectedAction,
    });
  };

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_233_EXPORT_FORMNAME,
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <FormExportButton
      formname={ICS_233_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-233CG"
      disabled={isExporting}
    />
  );

  const body = (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_233" />

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS233_BOX_LABELS.incidentName}
          testId="ics233-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS233_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics233-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS233_BOX_LABELS.incidentLocation}
          testId="ics233-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS233_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics233-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS233_BOX_LABELS.operationalPeriod}
          testId="ics233-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {ICS233_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics233-field-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS233_BOX_LABELS.actions}
        testId="ics233-section-actions"
        className="tw:mt-0"
      >
        <div data-section="actions">
          <ActionsWorkspace
            scope={INCIDENT}
            storageKey="ics233-actions-view"
            canEdit={canEditCards}
            onEditAction={setSelectedAction}
            toolbar={
              canEditCards ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={creatingTask}
                  onClick={addAction}
                >
                  <Plus className="tw:size-4" />
                  Add Action
                </Button>
              ) : null
            }
          />
        </div>
      </Ics204CardSection>
    </div>
  );

  const drawers = (
    <>
      {canEditCards && !hwcg && showAddDrawer ? (
        <ActionDrawer scope={INCIDENT} toggle={() => setShowAddDrawer(false)} />
      ) : null}
      {canEditCards && selectedAction ? (
        <ActionDrawer
          scope={INCIDENT}
          selectedEntity={selectedAction}
          toggle={() => setSelectedAction(null)}
        />
      ) : null}
    </>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return (
      <>
        <div
          className="dark content tw:text-foreground mt-2"
          data-tour="ics233-card"
        >
          {body}
        </div>
        {drawers}
      </>
    );
  }

  return (
    <>
      <Card
        data-testid="uscg-233-card"
        data-tour="ics233-card"
        data-form-family="uscg"
        className="dark tw:bg-background tw:text-foreground"
      >
        <CardHeader>
          <CardTitle>{getIncidentCardTitle('openActionsTracker')}</CardTitle>
          <CardAction>{exportActions}</CardAction>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
      {drawers}
    </>
  );
};

export default OpenActionsTracker;
